<script setup>
/**
 * NewWhatsAppChatFlow
 *
 * Content of the "New WhatsApp Chat" popover (see `NewWhatsAppChatLauncher.vue`
 * for the floating trigger that mounts this). Drives a 3-step flow:
 *
 *   1. "search"   - search existing contacts, or enter a new phone number
 *   2. "preview"  - confirm the contact and the WhatsApp inbox they'll be
 *                   reached on
 *   3. "template" - pick a WhatsApp template (reuses the existing
 *                   `WhatsAppOptions`/`WhatsappTemplate`/`WhatsAppTemplateParser`
 *                   chain from the general compose flow) and send
 *
 * Every piece of contact search/create, inbox resolution, and message
 * payload building is re-used from `composeConversationHelper.js` via
 * `newWhatsAppChatHelper.js` (see that file) - this component only owns the
 * WhatsApp-specific step sequencing and phone-first UX around it. Sending
 * itself dispatches the same `contactConversations/create` Vuex action the
 * general compose popover already uses, so no backend or store changes were
 * needed for this feature.
 */
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { ExceptionWithMessage } from 'shared/helpers/CustomErrors';
import { debounce } from '@chatwoot/utils';

import Button from 'dashboard/components-next/button/Button.vue';
import WhatsAppOptions from 'dashboard/components-next/NewConversation/components/WhatsAppOptions.vue';
import PhoneSearchStep from 'dashboard/components-next/NewWhatsAppChat/components/PhoneSearchStep.vue';
import ContactPreviewCard from 'dashboard/components-next/NewWhatsAppChat/components/ContactPreviewCard.vue';
import {
  createContactSearcher,
  createNewContact,
  fetchContactableInboxes,
  mergeInboxDetails,
  prepareWhatsAppMessagePayload,
  findWhatsAppInbox,
  isValidWhatsAppPhoneNumber,
} from 'dashboard/components-next/NewWhatsAppChat/helpers/newWhatsAppChatHelper';

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();
const searchContacts = createContactSearcher();

const STEPS = { SEARCH: 'search', PREVIEW: 'preview', TEMPLATE: 'template' };
const step = ref(STEPS.SEARCH);

// --- Step 1: search / new number state ---
const searchQuery = ref('');
const searchResults = ref([]);
const isSearching = ref(false);
const phoneNumber = ref('');
const phoneError = ref('');
const isCreatingContact = ref(false);

// --- Step 2: preview state ---
const selectedContact = ref(null);
const isExistingContact = ref(false);
const isFetchingInboxes = ref(false);
const whatsappInbox = ref(null);

const currentUser = useMapGetter('getCurrentUser');
const inboxesList = useMapGetter('inboxes/getInboxes');
const contactConversationsUiFlags = useMapGetter(
  'contactConversations/getUIFlags'
);
const isSending = computed(() => contactConversationsUiFlags.value.isCreating);

const resetFlow = () => {
  step.value = STEPS.SEARCH;
  searchQuery.value = '';
  searchResults.value = [];
  phoneNumber.value = '';
  phoneError.value = '';
  selectedContact.value = null;
  isExistingContact.value = false;
  whatsappInbox.value = null;
};

const runSearch = debounce(
  async query => {
    isSearching.value = true;
    try {
      const results = await searchContacts(query);
      // null means a newer search superseded this one - keep current results.
      if (results === null) return;
      searchResults.value = results;
    } catch (error) {
      searchResults.value = [];
    } finally {
      isSearching.value = false;
    }
  },
  400,
  false
);

const handleSearchQueryUpdate = query => {
  searchQuery.value = query;
  if (query.trim().length < 2) {
    searchResults.value = [];
    return;
  }
  runSearch(query);
};

/**
 * Resolves which of the contact's inboxes (if any) is a WhatsApp inbox,
 * merging in the account's full inbox list so the UI has display details
 * (name, phone number) to show, not just IDs.
 */
const resolveWhatsAppInbox = async contactId => {
  isFetchingInboxes.value = true;
  try {
    const contactableInboxes = await fetchContactableInboxes(contactId);
    const merged = mergeInboxDetails(contactableInboxes, inboxesList.value);
    selectedContact.value = {
      ...selectedContact.value,
      contactInboxes: merged,
    };
    whatsappInbox.value = findWhatsAppInbox(merged);
  } catch (error) {
    whatsappInbox.value = null;
  } finally {
    isFetchingInboxes.value = false;
  }
};

const selectContact = async contact => {
  selectedContact.value = contact;
  isExistingContact.value = true;
  step.value = STEPS.PREVIEW;
  await resolveWhatsAppInbox(contact.id);
};

const startNewChat = async () => {
  phoneError.value = '';
  if (!isValidWhatsAppPhoneNumber(phoneNumber.value)) {
    phoneError.value = t('NEW_WHATSAPP_CHAT.SEARCH_STEP.PHONE_INVALID');
    return;
  }

  isCreatingContact.value = true;
  try {
    // Safety net: search for an exact match before creating a duplicate
    // contact for a number that's already in the account.
    const matches = await searchContacts(phoneNumber.value, {
      skipMinLength: true,
    });
    const existingMatch = (matches || []).find(
      contact => contact.phoneNumber === phoneNumber.value
    );

    if (existingMatch) {
      await selectContact(existingMatch);
      return;
    }

    const newContact = await createNewContact(phoneNumber.value);
    selectedContact.value = newContact;
    isExistingContact.value = false;
    step.value = STEPS.PREVIEW;
    await resolveWhatsAppInbox(newContact.id);
  } catch (error) {
    useAlert(t('NEW_WHATSAPP_CHAT.SEARCH_STEP.CONTACT_CREATE_ERROR'));
  } finally {
    isCreatingContact.value = false;
  }
};

const backToSearch = () => {
  step.value = STEPS.SEARCH;
  selectedContact.value = null;
  whatsappInbox.value = null;
};

const continueToTemplate = () => {
  if (!whatsappInbox.value) return;
  step.value = STEPS.TEMPLATE;
};

const backToPreview = () => {
  step.value = STEPS.PREVIEW;
};

const handleTemplateSend = async ({ message, templateParams }) => {
  const payload = prepareWhatsAppMessagePayload({
    targetInbox: whatsappInbox.value,
    selectedContact: selectedContact.value,
    message,
    templateParams,
    currentUser: currentUser.value,
  });

  try {
    const data = await store.dispatch('contactConversations/create', {
      params: payload,
      isFromWhatsApp: true,
    });
    const action = {
      type: 'link',
      to: `/app/accounts/${data.account_id}/conversations/${data.id}`,
      message: t('NEW_WHATSAPP_CHAT.SUCCESS_ACTION_LABEL'),
    };
    useAlert(t('NEW_WHATSAPP_CHAT.SUCCESS_MESSAGE'), action);
    resetFlow();
    emit('close');
  } catch (error) {
    useAlert(
      error instanceof ExceptionWithMessage
        ? error.data
        : t('NEW_WHATSAPP_CHAT.SEND_ERROR')
    );
  }
};

onMounted(resetFlow);
</script>

<template>
  <div class="flex flex-col w-[22rem] max-h-[32rem]">
    <header class="flex items-center justify-between px-4 pt-4 pb-2">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('NEW_WHATSAPP_CHAT.TITLE') }}
      </h3>
    </header>

    <PhoneSearchStep
      v-if="step === STEPS.SEARCH"
      v-model:search-query="searchQuery"
      v-model:phone-number="phoneNumber"
      :search-results="searchResults"
      :is-searching="isSearching"
      :is-creating-contact="isCreatingContact"
      :phone-error="phoneError"
      @update:search-query="handleSearchQueryUpdate"
      @select-contact="selectContact"
      @start-new-chat="startNewChat"
    />

    <ContactPreviewCard
      v-else-if="step === STEPS.PREVIEW"
      :contact="selectedContact"
      :whatsapp-inbox="whatsappInbox"
      :is-fetching-inboxes="isFetchingInboxes"
      :is-existing-contact="isExistingContact"
      @back="backToSearch"
      @continue="continueToTemplate"
    />

    <div v-else-if="step === STEPS.TEMPLATE" class="flex flex-col gap-3 p-4">
      <ContactPreviewCard
        :contact="selectedContact"
        :whatsapp-inbox="whatsappInbox"
        :is-fetching-inboxes="false"
        :is-existing-contact="isExistingContact"
        compact
      />
      <p v-if="isSending" class="text-xs text-n-slate-11">
        {{ t('NEW_WHATSAPP_CHAT.SENDING') }}
      </p>
      <WhatsAppOptions
        v-else
        :inbox-id="whatsappInbox.id"
        @send-message="handleTemplateSend"
      />
      <Button
        variant="ghost"
        color="slate"
        size="sm"
        :disabled="isSending"
        :label="t('NEW_WHATSAPP_CHAT.BACK')"
        @click="backToPreview"
      />
    </div>
  </div>
</template>
